import { useMemo, useState } from 'react'

import type {
  CustomFieldRenderProps,
  F0FormSubmitResult,
  F0SectionConfig,
} from '@factorialco/f0-react'
import { F0Select, f0FormField, useF0FormDefinition } from '@factorialco/f0-react'
import { QueryWorkingConditionsRolesAndLocationsDocument } from 'generated/resources/graphql'
import { z } from 'zod'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import CurrencySelector from 'modules/finance/components/selectors/CurrencySelector'
import { Salary, SalaryPeriodicity, Workplace } from 'modules/job_catalog/lib/salary'
import {
  defaultCurrency,
  defaultPeriodicity,
  salaryToCents,
  salaryToString,
} from 'modules/job_catalog/lib/salaryUtils'

import { getSalaryModalValidationIssues } from './validation'

type WorkplaceOption = { label: string; value: string; disabled: boolean }

type WorkplaceSelectItem = { id: string | number; name: string }

const amountField = (
  label: string,
  row: string,
  currency: string,
  renderIf: ({ values }: { values: Record<string, unknown> }) => boolean
) =>
  f0FormField<z.ZodOptional<z.ZodNumber>>(z.number().optional(), {
    label,
    row,
    fieldType: 'money',
    currency,
    renderIf,
  })

const currencyField = (label: string, setSelectedCurrency: (currency: string) => void) =>
  f0FormField(z.string().min(1), {
    label,
    row: 'periodicity-currency',
    fieldType: 'custom',
    render: ({ value, onChange, disabled, error }: CustomFieldRenderProps<string>) => (
      <CurrencySelector
        label={label}
        value={value ?? ''}
        onChange={(nextCurrency) => {
          setSelectedCurrency(nextCurrency)
          onChange(nextCurrency)
        }}
        disabled={disabled}
        error={error}
      />
    ),
  })

const workplaceField = (options: WorkplaceOption[], mandatory: boolean, loading: boolean) =>
  f0FormField(z.array(z.string()).optional(), {
    label: i18n.t('job_catalog.nodes.salary_modal.workplace.title'),
    fieldType: 'custom',
    render: ({
      value,
      onChange,
      disabled,
      error,
    }: CustomFieldRenderProps<string[] | undefined>) => (
      <F0Select
        size="md"
        label={i18n.t('job_catalog.nodes.salary_modal.workplace.title')}
        required={mandatory}
        multiple
        value={value ?? []}
        onChange={(nextValue: string | string[]) =>
          onChange(Array.isArray(nextValue) ? nextValue : [])
        }
        placeholder={i18n.t('job_catalog.nodes.salary_modal.workplace.all')}
        options={options}
        showSearchBox
        disableSelectAll
        disabled={disabled}
        loading={loading}
        error={error}
      />
    ),
  })

export const buildWorkplaceOptions = (
  workplaces: WorkplaceSelectItem[],
  disabledWorkplaces: WorkplaceSelectItem[]
): WorkplaceOption[] => {
  const disabledWorkplaceIds = new Set(disabledWorkplaces.map(({ id }) => String(id)))

  return workplaces.map((workplace) => {
    const workplaceId = String(workplace.id)
    const isDisabled = disabledWorkplaceIds.has(workplaceId)

    return {
      label: workplace.name,
      value: workplaceId,
      disabled: isDisabled,
    }
  })
}

const buildSalaryModalSchema = ({
  workplaceOptions,
  workplacesMandatory,
  workplacesLoading,
  selectedCurrency,
  setSelectedCurrency,
  salaryPeriodicityOptions,
  salaryTypeOptions,
}: {
  workplaceOptions: WorkplaceOption[]
  workplacesMandatory: boolean
  workplacesLoading: boolean
  selectedCurrency: string
  setSelectedCurrency: (currency: string) => void
  salaryPeriodicityOptions: Array<{ label: string; value: string }>
  salaryTypeOptions: Array<{ label: string; value: 'range' | 'fixed' }>
}) =>
  z
    .object({
      workplaceIds: workplaceField(workplaceOptions, workplacesMandatory, workplacesLoading),
      periodicity: f0FormField(z.string().min(1), {
        label: i18n.t('job_catalog.nodes.salary_modal.periodicity'),
        row: 'periodicity-currency',
        fieldType: 'select',
        options: salaryPeriodicityOptions,
      }),
      currency: currencyField(i18n.t('finance.currency'), setSelectedCurrency),
      type: f0FormField(z.enum(['range', 'fixed']), {
        label: i18n.t('job_catalog.nodes.salary_modal.pay_structure'),
        fieldType: 'custom',
        fieldConfig: salaryTypeOptions,
        customFieldName: 'salary-type-select',
      }),
      minAmount: amountField(
        String(i18n.t('job_catalog.nodes.salary_modal.min_amount')),
        'salary-amounts',
        selectedCurrency,
        ({ values }) => values.type === 'range'
      ),
      maxAmount: amountField(
        String(i18n.t('job_catalog.nodes.salary_modal.max_amount')),
        'salary-amounts',
        selectedCurrency,
        ({ values }) => values.type === 'range'
      ),
      grossSalary: amountField(
        String(i18n.t('job_catalog.nodes.salary_modal.gross_salary')),
        'salary-amounts',
        selectedCurrency,
        ({ values }) => values.type === 'fixed'
      ),
    })
    .superRefine((data, ctx) => {
      getSalaryModalValidationIssues(data, workplacesMandatory).forEach((issue) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: issue.message,
          path: [issue.path],
        })
      })
    })

type SalaryModalSchema = ReturnType<typeof buildSalaryModalSchema>

type SalaryModalValues = z.infer<SalaryModalSchema>

interface UseFormParams {
  name: string
  initialSalary?: Salary
  availableWorkplaces: Workplace[]
  disabledWorkplaces: Workplace[]
  workplacesMandatory: boolean
  createAction: (salary: Salary, nodeUuid: string) => Promise<boolean>
  updateAction: (salary: Salary, nodeUuid: string) => Promise<boolean>
  nodeId: string
  onSubmit: () => void
}

export const useForm = ({
  name,
  initialSalary,
  availableWorkplaces,
  disabledWorkplaces,
  workplacesMandatory,
  createAction,
  updateAction,
  nodeId,
  onSubmit,
}: UseFormParams) => {
  const { data, loading: workplacesLoading } = useQuery(
    QueryWorkingConditionsRolesAndLocationsDocument
  )

  const workplaces = useMemo(() => {
    if (data?.locations?.locations) {
      return data.locations.locations.map((workplace) => ({
        id: String(workplace.id),
        name: workplace.name,
      }))
    }

    return availableWorkplaces.map((workplace) => ({
      id: String(workplace.id),
      name: workplace.name,
    }))
  }, [availableWorkplaces, data])

  const workplaceOptions = useMemo(
    () => buildWorkplaceOptions(workplaces, disabledWorkplaces),
    [workplaces, disabledWorkplaces]
  )

  const salaryPeriodicityOptions: Array<{ label: string; value: string }> = useMemo(
    () => [
      {
        label: i18n.t('job_catalog.nodes.salary_modal.periodicities.yearly'),
        value: SalaryPeriodicity.yearly,
      },
      {
        label: i18n.t('job_catalog.nodes.salary_modal.periodicities.monthly'),
        value: SalaryPeriodicity.monthly,
      },
      {
        label: i18n.t('job_catalog.nodes.salary_modal.periodicities.weekly'),
        value: SalaryPeriodicity.weekly,
      },
      {
        label: i18n.t('job_catalog.nodes.salary_modal.periodicities.daily'),
        value: SalaryPeriodicity.daily,
      },
      {
        label: i18n.t('job_catalog.nodes.salary_modal.periodicities.hourly'),
        value: SalaryPeriodicity.hourly,
      },
    ],
    []
  )

  const salaryTypeOptions: Array<{ label: string; value: 'range' | 'fixed' }> = useMemo(
    () => [
      {
        label: i18n.t('job_catalog.nodes.salary_modal.types.range'),
        value: 'range',
      },
      {
        label: i18n.t('job_catalog.nodes.salary_modal.types.fixed'),
        value: 'fixed',
      },
    ],
    []
  )

  const initialRange = initialSalary?.gross == null
  const currency = initialSalary?.currency ?? defaultCurrency
  const [selectedCurrency, setSelectedCurrency] = useState(currency)

  const schema = useMemo(
    () =>
      buildSalaryModalSchema({
        workplaceOptions,
        workplacesMandatory,
        workplacesLoading,
        selectedCurrency,
        setSelectedCurrency,
        salaryPeriodicityOptions,
        salaryTypeOptions,
      }),
    [
      workplaceOptions,
      workplacesMandatory,
      workplacesLoading,
      selectedCurrency,
      salaryPeriodicityOptions,
      salaryTypeOptions,
    ]
  )

  const defaultValues: SalaryModalValues = useMemo(
    () => ({
      workplaceIds: initialSalary?.workplaces?.map((workplace) => String(workplace.id)) ?? [],
      periodicity: initialSalary?.periodicity ?? defaultPeriodicity,
      currency,
      type: initialRange ? 'range' : 'fixed',
      minAmount: initialSalary?.min != null ? Number(salaryToString(initialSalary.min)) : undefined,
      maxAmount: initialSalary?.max != null ? Number(salaryToString(initialSalary.max)) : undefined,
      grossSalary:
        initialSalary?.gross != null ? Number(salaryToString(initialSalary.gross)) : undefined,
    }),
    [currency, initialRange, initialSalary]
  )

  const sections: Record<string, F0SectionConfig> = useMemo(
    () => ({
      salary: {
        title: i18n.t('job_catalog.nodes.salary_modal.title', { name }),
        description: i18n.t('job_catalog.nodes.salary_modal.description_workplaces'),
      },
    }),
    [name]
  )

  const formDefinition = useF0FormDefinition({
    name: 'job-catalog-salary-modal',
    schema,
    sections,
    defaultValues,
    errorTriggerMode: 'on-blur',
    onSubmit: async ({ data: formData }): Promise<F0FormSubmitResult> => {
      const selectedWorkplaces = workplaces.filter((workplace) =>
        (formData.workplaceIds ?? []).includes(String(workplace.id))
      )

      const salary: Salary =
        formData.type === 'fixed'
          ? {
              id: initialSalary?.id,
              currency: formData.currency,
              periodicity: formData.periodicity,
              workplaces: selectedWorkplaces,
              gross: salaryToCents(String(formData.grossSalary ?? 0)) ?? 0,
            }
          : {
              id: initialSalary?.id,
              currency: formData.currency,
              periodicity: formData.periodicity,
              workplaces: selectedWorkplaces,
              min: salaryToCents(String(formData.minAmount ?? 0)) ?? 0,
              max: salaryToCents(String(formData.maxAmount ?? 0)) ?? 0,
            }

      const success = initialSalary
        ? await updateAction(salary, nodeId)
        : await createAction(salary, nodeId)

      if (!success) return { success: false }

      onSubmit()
      return { success: true }
    },
    submitConfig: { hideSubmitButton: true },
  })

  return { formDefinition }
}
